"use client"

import * as React from "react"
import { cva } from "class-variance-authority"
import {
  motion,
  useMotionTemplate,
  useScroll,
  useTransform,
} from "motion/react"

import { cn } from "../../../lib/utils"

/* ---------------------------------- */
/* Variants */
/* ---------------------------------- */
const cardVariants = cva("absolute will-change-transform", {
  variants: {
    variant: {
      dark:
        "flex size-full flex-col items-center justify-center gap-6 rounded-2xl border border-stone-700/50 bg-accent-foreground/80 p-6 backdrop-blur-md",
      light:
        "flex size-full flex-col items-center justify-center gap-6 rounded-2xl border bg-accent bg-background/80 p-6 backdrop-blur-md",
    },
  },
  defaultVariants: {
    variant: "light",
  },
})

/* ---------------------------------- */
/* Scroll Context */
/* ---------------------------------- */
const ContainerScrollContext = React.createContext(undefined)

function useContainerScrollContext() {
  const context = React.useContext(ContainerScrollContext)
  if (!context) {
    throw new Error(
      "useContainerScrollContext must be used inside ContainerScroll"
    )
  }
  return context
}

/* ---------------------------------- */
/* ContainerScroll */
/* ---------------------------------- */
export function ContainerScroll({ children, className, style, ...props }) {
  const scrollRef = React.useRef(null)

  const { scrollYProgress } = useScroll({
    target: scrollRef,
    offset: ["start center", "end end"],
  })

  return (
    <ContainerScrollContext.Provider value={{ scrollYProgress }}>
      <div
        ref={scrollRef}
        className={cn("relative w-full", className)}
        style={{ perspective: "1000px", ...style }}
        {...props}
      >
        {children}
      </div>
    </ContainerScrollContext.Provider>
  )
}

/* ---------------------------------- */
/* CardsContainer */
/* ---------------------------------- */
export function CardsContainer({ children, className, style, ...props }) {
  return (
    <div
      className={cn("relative", className)}
      style={{ perspective: "1000px", ...style }}
      {...props}
    >
      {children}
    </div>
  )
}

/* ---------------------------------- */
/* CardTransformed */
/* ---------------------------------- */
export const CardTransformed = React.forwardRef(
  (
    {
      arrayLength,
      index,
      incrementY = 10,
      incrementZ = 10,
      incrementRotation = -index + 90,
      className,
      variant,
      style,
      ...props
    },
    ref
  ) => {
    const { scrollYProgress } = useContainerScrollContext()

    const start = index / (arrayLength + 1)
    const end = (index + 1) / (arrayLength + 1)
    const range = [start, end]
    const rotateRange = [range[0] - 1.5, range[1] / 1.5]

    const y = useTransform(scrollYProgress, range, ["0%", "-180%"])
    const rotate = useTransform(scrollYProgress, rotateRange, [
      incrementRotation,
      0,
    ])

    const transform = useMotionTemplate`
      translateZ(${index * incrementZ}px)
      translateY(${y})
      rotate(${rotate}deg)
    `

    const dx = useTransform(scrollYProgress, rotateRange, [4, 0])
    const dy = useTransform(scrollYProgress, rotateRange, [4, 12])
    const blur = useTransform(scrollYProgress, rotateRange, [2, 24])
    const alpha = useTransform(scrollYProgress, rotateRange, [0.15, 0.2])

    const filter =
      variant === "light"
        ? useMotionTemplate`drop-shadow(${dx}px ${dy}px ${blur}px rgba(0,0,0,${alpha}))`
        : "none"

    return (
      <motion.div
        ref={ref}
        layout="position"
        style={{
          top: index * incrementY,
          transform,
          zIndex: (arrayLength - index) * incrementZ,
          backfaceVisibility: "hidden",
          filter,
          ...style,
        }}
        className={cn(cardVariants({ variant }), className)}
        {...props}
      />
    )
  }
)

CardTransformed.displayName = "CardTransformed"

/* ---------------------------------- */
/* ReviewStars */
/* ---------------------------------- */
export const ReviewStars = React.forwardRef(
  ({ rating, maxRating = 5, className, ...props }, ref) => {
    const filledStars = Math.floor(rating)
    const fractional = rating - filledStars
    const emptyStars =
      maxRating - filledStars - (fractional > 0 ? 1 : 0)

    return (
      <div
        ref={ref}
        className={cn("flex items-center gap-2", className)}
        {...props}
      >
        <div className="flex items-center">
          {[...Array(filledStars)].map((_, i) => (
            <Star key={`filled-${i}`} />
          ))}

          {fractional > 0 && <HalfStar percent={fractional * 100} />}

          {[...Array(emptyStars)].map((_, i) => (
            <Star key={`empty-${i}`} className="text-gray-300" />
          ))}
        </div>
        <span className="sr-only">{rating}</span>
      </div>
    )
  }
)

ReviewStars.displayName = "ReviewStars"

/* ---------------------------------- */
/* Star SVGs */
/* ---------------------------------- */
function Star({ className = "text-current" }) {
  return (
    <svg
      className={cn("size-4", className)}
      fill="currentColor"
      viewBox="0 0 20 20"
    >
      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.54-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
    </svg>
  )
}

function HalfStar({ percent }) {
  return (
    <svg className="size-4" viewBox="0 0 20 20">
      <defs>
        <linearGradient id="half">
          <stop offset={`${percent}%`} stopColor="currentColor" />
          <stop offset={`${percent}%`} stopColor="#d1d5db" />
        </linearGradient>
      </defs>
      <path
        fill="url(#half)"
        d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.175 0l-3.37 2.448c-.784.57-1.838-.197-1.54-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.05 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z"
      />
    </svg>
  )
}
